import { useEffect } from 'react';
import {
  Spinner,
  ButtonSize,
  SecondaryButton,
  PrimaryButton,
  Typography,
  ButtonVariant,
} from '@uni-of-exeter/digital.lib.react.ui.kit';
import './bookmarked-resources-page.css';
import { APP_TEXT } from '../const/app-text';
import { colorHexWithOpacity } from '../const/colors';
import { useErrorContext } from '../components/error';
import { useAuthContext } from '../auth/auth-provider';
import FeedbackBlock from '../components/feedback-block';
import { PageWrapper } from '../components/page-wrapper';
import { PageTracker } from '../components/page-tracker';
import { getCategoryDataByName } from '../utils/hardcodes';
import { ResourceTable } from '../components/resource-table';
import { useBreakpoint } from '../hooks/utils/use-breakpoint';
import { useBookmarkPage } from '../hooks/custom/bookmarking/use-bookmark-page';
import { ResourceTableMobile } from '../components/resource-table-mobile';
import { APP_URLS } from '../routes/paths';
import { useRouter } from '../routes/hooks';
import { useLibraryFilters } from '../hooks/utils';
import { useGetResourceFacets } from '../hooks/query/learning-api/use-resource';
import { ResourceFilter } from '../components/resource-filter';
import { useBookmarks } from '../hooks/custom/bookmarking/use-bookmarks';
import ConfirmationModal from '../components/modals/confirmation-modal';

const pageText = APP_TEXT.pages.bookmarkedResources;
const pageSize = 20;

const BookmarksPage = () => {
  const { setError } = useErrorContext();
  const { userId } = useAuthContext();
  const router = useRouter();
  const isLgDown = useBreakpoint('lgxl', 'down');
  const { data: facets, isError: resourceFacetsError } = useGetResourceFacets();
  const {
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilters,
    uniqueSkillCategoryOptions,
    mediaTypeOptions,
    locationOptions,
  } = useLibraryFilters(facets);
  const { resources, isLoading, isError, deleteAllBookmarksConfirmationModal } =
    useBookmarkPage(filters);

  const { handleDeleteAllBookmarks } = useBookmarks();

  useEffect(() => {
    if (isError || resourceFacetsError) {
      setError({
        code: 500,
        message: 'Failed to fetch resources',
      });
    }
  }, [isError, setError]);

  const renderLoading = (
    <div className="loadingWrapper">
      <Spinner aria-label="Loading your bookmarks..." />
    </div>
  );

  const resourceCount = resources?.length ?? 0;

  const getBookmarksPageSubtitle = () => {
    if (resourceCount === 0 && hasActiveFilters) return pageText.noBookmarks.filteredSubtitle;
    if (resourceCount > 0) {
      const subtitle = pageText.subtitle.replace(
        '{bookmarkedResources}',
        `${resourceCount} resource${resourceCount > 1 ? 's' : ''}`
      );
      return subtitle;
    }
  };
  const subtitle = getBookmarksPageSubtitle();

  return (
    <PageWrapper
      className="bookmarksPageWrapper"
      title={pageText.metadata.title}
    >
      <PageTracker
        pageName="BookmarksPage"
        userId={userId}
      />
      <div className="bookmarksPageContent">
        <div className="bookmarksPageHeader">
          <Typography variant="h1.bold">{pageText.title}</Typography>
          <div className="bookmarksPageContentRow">
            <div className="bookmarksPageContentHeader">
              <Typography
                variant={'h6'}
                fontWeight={700}
                color={'#6F6F6F'}
              >
                {subtitle}
              </Typography>
            </div>
            <div className="bookmarksPageHeaderActions">
              {resources && resourceCount > 0 && (
                <SecondaryButton
                  className="bookmarksPageHeaderDeleteAllResourcesButton"
                  buttonSize={ButtonSize.Medium}
                  variant={ButtonVariant.Outline}
                  onClick={deleteAllBookmarksConfirmationModal.onTrue}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.2 5.2H3.8V12.9C3.8 13.0223 3.86816 13.2033 4.03242 13.3676C4.19668 13.5318 4.37766 13.6 4.5 13.6H11.5C11.6223 13.6 11.8033 13.5318 11.9676 13.3676C12.1318 13.2033 12.2 13.0223 12.2 12.9V5.2ZM5.9 10.8V8C5.9 7.6134 6.2134 7.3 6.6 7.3C6.9866 7.3 7.3 7.6134 7.3 8V10.8C7.3 11.1866 6.9866 11.5 6.6 11.5C6.2134 11.5 5.9 11.1866 5.9 10.8ZM8.7 10.8V8C8.7 7.6134 9.0134 7.3 9.4 7.3C9.7866 7.3 10.1 7.6134 10.1 8V10.8C10.1 11.1866 9.7866 11.5 9.4 11.5C9.0134 11.5 8.7 11.1866 8.7 10.8ZM10.1 3.1C10.1 2.97766 10.0318 2.79668 9.86758 2.63242C9.70332 2.46816 9.52234 2.4 9.4 2.4H6.6C6.47766 2.4 6.29668 2.46816 6.13242 2.63242C5.96816 2.79668 5.9 2.97766 5.9 3.1V3.8H10.1V3.1ZM11.5 3.8H14.3C14.6866 3.8 15 4.1134 15 4.5C15 4.8866 14.6866 5.2 14.3 5.2H13.6V12.9C13.6 13.4777 13.3182 13.9967 12.9574 14.3574C12.5967 14.7182 12.0777 15 11.5 15H4.5C3.92234 15 3.40332 14.7182 3.04258 14.3574C2.68184 13.9967 2.4 13.4777 2.4 12.9V5.2H1.7C1.3134 5.2 1 4.8866 1 4.5C1 4.1134 1.3134 3.8 1.7 3.8H4.5V3.1C4.5 2.52234 4.78184 2.00332 5.14258 1.64258C5.50332 1.28184 6.02234 1 6.6 1H9.4C9.97766 1 10.4967 1.28184 10.8574 1.64258C11.2182 2.00332 11.5 2.52234 11.5 3.1V3.8Z"
                      fill="#003C3C"
                    />
                  </svg>
                  {pageText.deleteAllBookmarks}
                </SecondaryButton>
              )}
            </div>
          </div>
        </div>

        {resources?.length === 0 && !hasActiveFilters && (
          <div>
            <div className="noBookmarksWrapper">
              <img
                className="noBookmarksFoundImg"
                src="/assets/no_bookmarks_found_image.png"
                alt="No bookmarks found"
              />
              <Typography variant="h3.bold">{pageText.noBookmarks.subtitle}</Typography>
              <PrimaryButton
                className="browseResourcesBtn"
                aria-label={pageText.noBookmarks.buttonText}
                buttonSize={ButtonSize.Large}
                onClick={() => {
                  router.push(APP_URLS.library());
                }}
                label={pageText.noBookmarks.buttonText}
              />
            </div>
            {/* Put quiz section in its own component */}
            <div className="takeQuizSection">
              <Typography
                variant="h4"
                className="takeQuizSectionTitle"
              >
                {pageText.noBookmarks.takeQuizSection.title}
              </Typography>
              <SecondaryButton
                className="takeQuizSectionButton"
                aria-label={pageText.noBookmarks.takeQuizSection.buttonText}
                buttonSize={ButtonSize.Large}
                onClick={() => {
                  router.push(APP_URLS.introPage());
                }}
                label={pageText.noBookmarks.takeQuizSection.buttonText}
              />
            </div>
          </div>
        )}

        {resources && (hasActiveFilters || resources?.length > 0) && (
          <ResourceFilter
            filters={filters}
            skillOptions={uniqueSkillCategoryOptions}
            updateFilters={updateFilters}
            mediaTypeOptions={mediaTypeOptions}
            locationOptions={locationOptions}
            hasActiveFilters={hasActiveFilters}
            resetFilters={resetFilters}
          />
        )}

        {resources && (hasActiveFilters || resources?.length > 0) && (
          <div className="tableWrapper">
            {isLgDown ? (
              <ResourceTableMobile
                resources={resources}
                isLoading={isLoading}
                isError={isError}
                getCategoryColor={(category: string) =>
                  colorHexWithOpacity(getCategoryDataByName(category).color, 0.5)
                }
                source="BookmarksPage"
                rowsToShow={pageSize}
                totalItems={resourceCount}
                enableBookmarkDeleteColumn={true}
                defaultPagination
              />
            ) : (
              <ResourceTable
                resources={resources}
                isLoading={isLoading}
                isError={isError}
                getCategoryColor={(category: string) =>
                  colorHexWithOpacity(getCategoryDataByName(category).color, 0.5)
                }
                source="BookmarksPage"
                rowsToShow={pageSize}
                totalItems={resourceCount}
                enableBookmarkDeleteColumn={true}
                defaultPagination
              />
            )}
          </div>
        )}
        {isLoading && renderLoading}
        <ConfirmationModal
          text={APP_TEXT.components.deleteAllBookmarksConfirmationModal}
          isOpen={deleteAllBookmarksConfirmationModal.value}
          onClose={deleteAllBookmarksConfirmationModal.onFalse}
          onConfirm={handleDeleteAllBookmarks}
        ></ConfirmationModal>
      </div>
      <FeedbackBlock />
    </PageWrapper>
  );
};

export default BookmarksPage;
